import React, { useEffect, useState } from "react";
import { FaPlus, FaUsers } from "react-icons/fa";
import Button from "../../components/Button/Button";
import ButtonEdit from "../../components/Button/ButtonEdit";
import ButtonDelete from "../../components/Button/ButtonDelete";
import {
  getAllActeursListWithGroup,
  deleteActeur,
} from "../../services/acteursServices";
import { getGroupsList } from "../../services/groupServices";
import CreateActeurModal from "./CreateActeurModal";
import EditActeurModal from "./EditActeurModal";
import ManageGroupModal from "./ManageGroupModal";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";

const ActeursPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const canManage =
    userRole === "Administrateur" || userRole === "Bureau";

  const [acteurs, setActeurs] = useState([]);
  const [displayedActeurs, setDisplayedActeurs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showManageGroupModal, setShowManageGroupModal] = useState(false);
  const [editActeurId, setEditActeurId] = useState(null);
  const [manageGroupActeur, setManageGroupActeur] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchActeurs = async () => {
    try {
      setIsLoading(true);
      const acteursData = await getAllActeursListWithGroup();
      setActeurs(acteursData);
      setDisplayedActeurs(acteursData);
    } catch (error) {
      console.error("Erreur lors du chargement des acteurs:", error);
      toast.error("Erreur lors du chargement des acteurs");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const groupsData = await getGroupsList();
      setGroups(groupsData);
    } catch (error) {
      console.error("Erreur lors du chargement des groupes:", error);
      toast.error("Erreur lors du chargement des groupes");
    }
  };

  useEffect(() => {
    fetchActeurs();
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterActeurs = (term, group) => {
    let filtered = acteurs;

    if (term) {
      const t = term.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.nom.toLowerCase().includes(t) ||
          a.prenom.toLowerCase().includes(t) ||
          (a.club_acteur || "").toLowerCase().includes(t)
      );
    }

    if (group) {
      filtered = filtered.filter((acteur) =>
        acteur.Groups?.some((g) => g.nom === group)
      );
    }

    setDisplayedActeurs(filtered);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    filterActeurs(value, selectedGroup);
  };

  const handleGroupChange = (e) => {
    const value = e.target.value;
    setSelectedGroup(value);
    filterActeurs(searchTerm, value);
  };

  const deleteActeurById = async (acteurId) => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer cet acteur ?"
      )
    )
      return;

    try {
      await deleteActeur(acteurId);
      toast.success("Acteur supprimé avec succès");
      const updated = acteurs.filter((a) => a.id !== acteurId);
      setActeurs(updated);
      setDisplayedActeurs((prev) => prev.filter((a) => a.id !== acteurId));
    } catch (error) {
      console.error("Erreur lors de la suppression de l'acteur:", error);
      toast.error("Erreur lors de la suppression de l'acteur");
    }
  };

  const handleAddActeur = () => {
    if (!canManage) {
      toast.error("Vous n'avez pas les droits pour ajouter un acteur.");
      return;
    }
    setShowCreateModal(true);
  };

  const openEditModal = (acteurId) => {
    if (!canManage) {
      toast.error("Vous n'avez pas les droits pour modifier cet acteur.");
      return;
    }
    setEditActeurId(acteurId);
    setShowEditModal(true);
  };

  const openManageGroupModal = (acteur) => {
    if (!canManage) {
      toast.error("Vous n'avez pas les droits pour gérer les groupes.");
      return;
    }
    setManageGroupActeur(acteur);
    setShowManageGroupModal(true);
  };

  const handleCloseManageGroupModal = () => {
    setShowManageGroupModal(false);
    setManageGroupActeur(null);
  };

  const toAbsoluteAvatar = (url) => {
    if (!url) return "/img/user_avatar.jpg";
    const s = String(url).trim();
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith("/")) return s;
    return s;
  };

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      {/* HEADER */}
      <div className="mx-auto w-full max-w-[1280px]">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/70 backdrop-blur mb-6">
          <div className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 px-5 py-6 sm:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xs sm:text-sm font-semibold tracking-[0.3em] text-white/80 uppercase">
                  ADMINISTRATEURS
                </h2>
                <h1 className="mt-2 text-xl sm:text-2xl md:text-3xl font-bold tracking-[0.15em] text-white font-montserrat uppercase">
                  Acteurs du Judo Girondin
                </h1>
                <p className="mt-2 text-xs sm:text-sm text-white/80 max-w-2xl">
                  Gérez les arbitres, commissaires, enseignants, dirigeants
                  et autres acteurs liés aux groupes de travail.
                </p>
              </div>

              <div className="flex flex-col items-center sm:items-end gap-2">
                <div className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-3 py-1.5 text-xs text-white">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/15">
                    <FaUsers className="text-sm" />
                  </span>
                  <span className="font-semibold">
                    {displayedActeurs.length} acteur
                    {displayedActeurs.length > 1 ? "s" : ""}
                  </span>
                </div>
                {canManage && (
                  <Button
                    text="Ajouter un acteur"
                    onClick={handleAddActeur}
                    icon={FaPlus}
                    className="!rounded-xl !shadow-md bg-white text-slate-900 hover:bg-slate-100 border-0 mt-1"
                  />
                )}
              </div>
            </div>

            {/* Filtres */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-[2fr,1.5fr] gap-3">
              <div>
                <label className="text-[0.7rem] uppercase tracking-wide text-white/80 mb-1 block">
                  Recherche (nom, prénom, club)
                </label>
                <input
                  className="w-full rounded-xl border border-white/40 bg-white/95 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  type="text"
                  placeholder="Rechercher un acteur..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <div>
                <label className="text-[0.7rem] uppercase tracking-wide text-white/80 mb-1 block">
                  Filtrer par groupe
                </label>
                <select
                  className="w-full rounded-xl border border-white/40 bg-white/95 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  value={selectedGroup}
                  onChange={handleGroupChange}
                >
                  <option value="">Tous les groupes</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.nom}>
                      {group.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="px-3 sm:px-4 pb-5 pt-4">
            <div className="overflow-x-auto shadow-sm rounded-2xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900">
              {isLoading ? (
                <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">
                  Chargement des acteurs...
                </div>
              ) : displayedActeurs.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">
                  Aucun acteur ne correspond à vos filtres.
                </div>
              ) : (
                <table className="table w-full font-montserrat text-sm">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                        Acteur
                      </th>
                      <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                        Coordonnées
                      </th>
                      <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider min-w-[260px]">
                        Groupes (R = Responsable)
                      </th>
                      <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                        Club
                      </th>
                      <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                        Groupe souhaité
                      </th>
                      <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                        Utilisateur lié
                      </th>
                      <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedActeurs.map((acteur) => (
                      <tr
                        key={acteur.id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/70"
                      >
                        {/* Acteur */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center gap-3">
                            <img
                              className="rounded-full w-12 h-12 object-cover ring-1 ring-slate-200 dark:ring-slate-600"
                              src={toAbsoluteAvatar(acteur.photo_url)}
                              alt={`${acteur.nom} ${acteur.prenom}`}
                            />
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-900 dark:text-slate-50">
                                {(acteur.nom || "").toUpperCase()}{" "}
                                {acteur.prenom || ""}
                              </div>
                              {acteur.fonction && (
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                  {acteur.fonction}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Coordonnées */}
                        <td className="px-4 py-3 align-top text-xs text-slate-700 dark:text-slate-200">
                          {acteur.email && (
                            <div className="truncate">{acteur.email}</div>
                          )}
                          {acteur.tel && (
                            <div className="mt-1 text-[0.75rem] text-slate-500">
                              {acteur.tel}
                            </div>
                          )}
                        </td>

                        {/* Groupes */}
                        <td className="px-4 py-3 align-top text-xs text-slate-700 dark:text-slate-200">
                          {acteur.Groups && acteur.Groups.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {acteur.Groups.map((group) => (
                                <span
                                  key={group.id}
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.7rem] ring-1 ${
                                    group.ActeurGroup.is_responsable
                                      ? "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60"
                                      : "bg-slate-50 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700"
                                  }`}
                                >
                                  {group.ActeurGroup.is_responsable && (
                                    <span className="mr-1 font-bold">R</span>
                                  )}
                                  <span className="truncate max-w-[160px]">
                                    {group.nom}
                                  </span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[0.7rem] italic text-slate-400">
                              Aucun groupe
                            </span>
                          )}
                        </td>

                        {/* Club */}
                        <td className="px-4 py-3 align-top text-xs text-slate-700 dark:text-slate-200">
                          {acteur.club_acteur || (
                            <span className="text-[0.7rem] italic text-slate-400">
                              —
                            </span>
                          )}
                        </td>

                        {/* Groupe souhaité */}
                        <td className="px-4 py-3 align-top text-xs text-slate-700 dark:text-slate-200">
                          {acteur.groupe_souhaite ? (
                            <span className="inline-flex rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-2 py-0.5 text-[0.7rem]">
                              {acteur.groupe_souhaite}
                            </span>
                          ) : (
                            <span className="text-[0.7rem] italic text-slate-400">
                              —
                            </span>
                          )}
                        </td>

                        {/* Utilisateur lié */}
                        <td className="px-4 py-3 align-top text-xs text-slate-700 dark:text-slate-200">
                          {acteur.Users && acteur.Users.length > 0 ? (
                            <div className="space-y-1">
                              {acteur.Users.map((u, idx) => (
                                <div key={`${acteur.id}-${idx}`}>
                                  {(u.nom || "").toUpperCase()} {u.prenom || ""}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[0.7rem] italic text-slate-400">
                              Aucun utilisateur lié
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-wrap gap-1">
                            {canManage && (
                              <>
                                <ButtonEdit
                                  onClick={() => openEditModal(acteur.id)}
                                  hoverLabel="Éditer"
                                />
                                <ButtonDelete
                                  onClick={() => deleteActeurById(acteur.id)}
                                  hoverLabel="Supprimer"
                                />
                                <Button
                                  onClick={() => openManageGroupModal(acteur)}
                                  className="btn-info !px-3 !py-1.5 text-xs flex items-center gap-1 rounded-lg"
                                  text="Groupes"
                                />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODALES */}
      <CreateActeurModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        refreshList={fetchActeurs}
      />
      <EditActeurModal
        isOpen={showEditModal}
        acteurId={editActeurId}
        onClose={() => setShowEditModal(false)}
        refreshList={fetchActeurs}
      />
      <ManageGroupModal
        isOpen={showManageGroupModal}
        onClose={handleCloseManageGroupModal}
        acteurId={manageGroupActeur?.id}
        acteurNom={manageGroupActeur?.nom}
        acteurPrenom={manageGroupActeur?.prenom}
        groups={groups}
        acteurGroups={manageGroupActeur?.Groups || []}
        refreshData={fetchActeurs}
      />

      <ToastContainer />
    </div>
  );
};

export default ActeursPage;
